// Bulk variant of exportSurveyToExcelTemplate: stamps the latest survey of
// every property in a project into its own copy of the picked Excel template
// and bundles the results into a zip, built through the download manager.

import JSZip from "jszip";
import type { BuildContext } from "@/features/exports/services/DownloadManagerContext";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import type { TemplateField } from "../pages/hooks/useTemplateOptions";
import { fetchLatestAttributes } from "@/features/exports/services/exportSurvey";
import { readSheetColumnFormulas, readSheetColumnText, stampXlsxTemplate } from "./xlsxTemplatePatcher";
import {
  buildGcEvaluationWrites,
  buildGcRestyleContext,
  buildGcRowRestyles,
  buildSurveyCellLookup,
  GC_EVALUATION_SHEET,
  SurveyAnswers,
} from "./gcEvaluationFill";
import { collectTokenWrites } from "./placeholderTokenFill";
import { collectFindingsWrites } from "./summaryFindingsFill";
import { collectUnitBreakdownWrites } from "./summaryUnitBreakdownFill";
import { fillSummaryPhotos } from "./summaryPhotoFill";
import { buildGcsProxy } from "./qaEvidenceProxy";
import { fetchReviewerEvidence, mergeReviewerEvidence } from "./fetchReviewerEvidence";
import type { ExcelCellMapping } from "./exportSurveyToExcelTemplate";
import {
  applyHvacAgeActionRule,
  augmentAnswersWithFormLabels,
  buildAnswerMap,
  collectLabelMatchedWrites,
  collectMappingCellWrites,
  collectTemplateCellWrites,
  deliverableFilenameBase,
  exportFilenameBase,
  mergeAssetUnitAnswers,
  mergeProdDataAnswers,
  readSurveyFormLabels,
  resolveDefaultSheet,
  SURVEY_FORM_LOCK,
} from "./exportSurveyToExcelTemplate";
import { fetchExportAssets, fetchExportProperties } from "./fetchExportProperty";
import { uploadExportToRIProject } from "./uploadExportToRIProject";

interface PropertyRef {
  id: string;
  name: string;
  address?: string | null;
}

export async function buildTemplatedSurveysBundle(
  args: {
    /** The picked .xlsx template's bytes; re-parsed per property since stamping mutates the workbook. */
    templateBuf: ArrayBuffer;
    templateFields: TemplateField[];
    properties: PropertyRef[];
    /** Project whose full property rows back the excelCellMappings resolution. */
    projectId: string;
    /** Template file extension — xlsm templates keep their macros. */
    templateExt?: "xlsx" | "xlsm";
    /** "download" (default) bundles a zip; "upload" pushes each file into its
     * property's RoboticImaging project (ref_id) and returns a text receipt. */
    destination?: "download" | "upload";
    excelCellMappings?: ExcelCellMapping[];
    /** Sheet for writes without a per-question excelTemplateSheet; resolved via resolveDefaultSheet when absent. */
    defaultSheet?: string | null;
  },
  ctx: BuildContext
): Promise<Blob> {
  const { templateBuf, templateFields, properties, projectId, excelCellMappings } = args;
  const defaultSheet = await resolveDefaultSheet(templateBuf, args.defaultSheet);
  const ext = args.templateExt ?? "xlsx";
  const uploadMode = args.destination === "upload";
  const receipt: string[] = [];

  ctx.setProgress(0, "Fetching surveys…");
  const rows = await fetchLatestAttributes(properties.map((p) => p.id));
  const surveyByProperty = new Map(rows.map((r) => [r.property_id, r.data as AttributeField[]]));
  const capturedAtByProperty = new Map(rows.map((r) => [r.property_id, r.captured_at]));
  const prodDataByProperty = new Map(rows.map((r) => [r.property_id, r.prod_data ?? null]));
  const surveyIdByProperty = new Map(rows.map((r) => [r.property_id, r.survey_id ?? null]));
  // One token for the whole batch — resolves reviewer-evidence (gs://) media.
  const gsProxy = await buildGcsProxy();
  // Property rows feed the mappings AND the [Address]/[Site #] token fill.
  const propertyInfoById = new Map(
    (await fetchExportProperties(projectId)).map((p) => [p.id, p])
  );
  // GC Evaluation row labels and cost formulas are per-template — read once.
  const gcLabels = await readSheetColumnText(templateBuf, GC_EVALUATION_SHEET, "B");
  const gcFormulas =
    gcLabels.size > 0
      ? await readSheetColumnFormulas(templateBuf, GC_EVALUATION_SHEET, "D")
      : new Map<number, string>();
  const gcRestyleCtx = gcLabels.size > 0 ? await buildGcRestyleContext(templateBuf, gcLabels) : null;
  const formLabels = await readSurveyFormLabels(templateBuf);

  const zip = new JSZip();
  const usedNames = new Map<string, number>();
  let exported = 0;
  let skipped = 0;

  for (let i = 0; i < properties.length; i++) {
    if (ctx.signal.aborted) break;
    const property = properties[i];
    const fields = surveyByProperty.get(property.id);
    if (!fields) {
      skipped++;
      continue;
    }

    const assets = await fetchExportAssets(property.id).catch(() => []);
    // Answer precedence: tree → prod_data (incl. tree-less questions) →
    // assets; then the template's ≥10 yrs = Replace rule corrects them.
    const answerMap = applyHvacAgeActionRule(
      mergeAssetUnitAnswers(
        mergeProdDataAnswers(buildAnswerMap(fields), prodDataByProperty.get(property.id)),
        assets
      ),
      fields
    );
    const { writes: mappedWrites } = collectTemplateCellWrites(templateFields, answerMap);
    // Label-matched fallback for questions the template doesn't map (HVAC 4–6).
    const labelWrites = collectLabelMatchedWrites(
      formLabels,
      new SurveyAnswers(answerMap),
      mappedWrites,
      defaultSheet
    );
    // N/A always stamps — the reviewer's answer_status is the gate.
    const writes = [...mappedWrites, ...labelWrites];
    const augmentedMap = augmentAnswersWithFormLabels(answerMap, formLabels, writes, defaultSheet);
    const mappingWrites = collectMappingCellWrites(excelCellMappings, {
      property: propertyInfoById.get(property.id) ?? null,
      capturedAt: capturedAtByProperty.get(property.id) ?? null,
    });
    const gc =
      gcLabels.size > 0
        ? buildGcEvaluationWrites(gcLabels, augmentedMap, {
            formulasByRow: gcFormulas,
            surveyCellValue: buildSurveyCellLookup(writes, defaultSheet),
          })
        : null;
    const surveyAnswers = new SurveyAnswers(augmentedMap);
    const tokenWrites = await collectTokenWrites(templateBuf, {
      property: propertyInfoById.get(property.id) ?? null,
      capturedAt: capturedAtByProperty.get(property.id) ?? null,
      answers: surveyAnswers,
    });
    const findingsWrites = await collectFindingsWrites(templateBuf, surveyAnswers);
    const breakdownWrites = await collectUnitBreakdownWrites(templateBuf, surveyAnswers);
    const { data: stamped } = await stampXlsxTemplate(
      templateBuf,
      [...tokenWrites, ...findingsWrites, ...breakdownWrites, ...writes, ...mappingWrites, ...(gc?.writes ?? [])],
      defaultSheet,
      gc ? buildGcRowRestyles(gc, gcRestyleCtx) : undefined,
      SURVEY_FORM_LOCK
    );
    const surveyRowId = surveyIdByProperty.get(property.id);
    const photoProdData = surveyRowId
      ? mergeReviewerEvidence(
          prodDataByProperty.get(property.id),
          await fetchReviewerEvidence(surveyRowId).catch(() => new Map())
        )
      : prodDataByProperty.get(property.id);
    const { data } = await fillSummaryPhotos(
      stamped,
      fields,
      assets,
      surveyAnswers,
      undefined,
      photoProdData,
      gsProxy
    );

    let name = deliverableFilenameBase(
      propertyInfoById.get(property.id)?.city,
      propertyInfoById.get(property.id)?.state,
      propertyInfoById.get(property.id)?.store_number,
      exportFilenameBase(property.name, property.address, property.id)
    );
    const seen = (usedNames.get(name) ?? 0) + 1;
    usedNames.set(name, seen);
    if (seen > 1) name = `${name} (${seen})`;
    const filename = `${name}.${ext}`;

    if (uploadMode) {
      const refId = propertyInfoById.get(property.id)?.ref_id;
      if (!refId) {
        receipt.push(`SKIPPED (no linked RI project): ${filename}`);
        skipped++;
      } else {
        try {
          const url = await uploadExportToRIProject(data, filename, refId);
          receipt.push(`UPLOADED: ${filename} → project ${refId}\n  ${url}`);
          exported++;
        } catch (err) {
          receipt.push(`FAILED: ${filename} → project ${refId} (${err instanceof Error ? err.message : err})`);
        }
      }
    } else {
      zip.file(filename, data);
      exported++;
    }
    ctx.setProgress(
      Math.round(((i + 1) / properties.length) * 100),
      `${exported} ${uploadMode ? "uploaded" : "exported"}${skipped > 0 ? `, ${skipped} skipped` : ""}`
    );
  }

  if (exported === 0) {
    throw new Error(
      uploadMode
        ? "Nothing uploaded — no property had both a survey and a linked RI project."
        : "None of this project's properties have a survey to export."
    );
  }

  if (uploadMode) {
    return new Blob([receipt.join("\n\n")], { type: "text/plain" });
  }
  // Workbooks are already deflate-compressed; recompressing wastes CPU.
  return zip.generateAsync({ type: "blob", compression: "STORE" });
}
